import { useContext } from 'react';
import { Button, Switch, TimePicker } from 'antd';
import Modal from 'antd/es/modal/Modal';
import { ClockCircleOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import UserEditionFormContext from '../../../contexts/forms/users/UserEditionFormContext';
import { DAYS_OF_WEEK, DAY_LABELS } from '../../../../domain/models/AvailabilityHours';
import { IAvailabilityHours } from '@leedpulse-monorepo/interfaces';

interface AvailabilityHoursModalProps {
  open: boolean;
  onClose: () => void;
  availabilityHours: IAvailabilityHours | null;
}

const AvailabilityHoursModal: React.FC<AvailabilityHoursModalProps> = ({
  open,
  onClose,
  availabilityHours,
}) => {
  const userEditionFormContext = useContext(UserEditionFormContext);

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ClockCircleOutlined size={20} style={{ marginRight: 6 }} />
          <span style={{ fontSize: '16px', fontWeight: 600 }}>Horaires de disponibilité</span>
        </div>
      }
      centered
      open={open}
      onCancel={onClose}
      footer={
        <Button type="primary" onClick={onClose} style={{ borderRadius: '6px' }}>
          Terminer
        </Button>
      }
      width={500}
    >
      <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '12px', marginTop: '16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {DAYS_OF_WEEK.map((day, index) => {
            const daySchedule = availabilityHours?.schedule[day];
            if (!daySchedule) return null;
            const isLast = index === DAYS_OF_WEEK.length - 1;

            return (
              <div
                key={day}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 0',
                  borderBottom: isLast ? 'none' : '1px solid #f0f0f0',
                }}
              >
                <div
                  style={{
                    width: '130px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Switch
                    checked={daySchedule.enabled}
                    onChange={() => userEditionFormContext.onDayToggle(day)}
                    size="small"
                  />
                  <span
                    style={{
                      fontWeight: 500,
                      color: daySchedule.enabled ? '#262626' : '#8c8c8c',
                      fontSize: '14px',
                    }}
                  >
                    {DAY_LABELS[day]}
                  </span>
                </div>

                <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  {!daySchedule.enabled ? (
                    <span style={{ color: '#bfbfbf', fontSize: '13px' }}>Fermé</span>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        alignItems: 'flex-end',
                      }}
                    >
                      {daySchedule.slots.map((slot, slotIndex) => (
                        <div
                          key={slotIndex}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                          <TimePicker
                            value={dayjs(slot.start, 'HH:mm')}
                            format="HH:mm"
                            minuteStep={30}
                            needConfirm={false}
                            allowClear={false}
                            size="small"
                            style={{ width: '75px', borderRadius: '4px' }}
                            onChange={(time) => {
                              if (time)
                                userEditionFormContext.onUpdateSlot(
                                  day,
                                  slotIndex,
                                  'start',
                                  time.format('HH:mm')
                                );
                            }}
                          />
                          <span style={{ color: '#8c8c8c', fontSize: '12px' }}>à</span>
                          <TimePicker
                            value={dayjs(slot.end, 'HH:mm')}
                            format="HH:mm"
                            minuteStep={30}
                            needConfirm={false}
                            allowClear={false}
                            size="small"
                            style={{ width: '75px', borderRadius: '4px' }}
                            onChange={(time) => {
                              if (time)
                                userEditionFormContext.onUpdateSlot(
                                  day,
                                  slotIndex,
                                  'end',
                                  time.format('HH:mm')
                                );
                            }}
                          />
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => userEditionFormContext.onRemoveSlot(day, slotIndex)}
                            style={{ padding: '0 4px', minWidth: 'auto' }}
                          />
                        </div>
                      ))}
                      {daySchedule.slots.length < 2 && (
                        <Button
                          type="dashed"
                          icon={<PlusOutlined />}
                          onClick={() => userEditionFormContext.onAddSlot(day)}
                          size="small"
                          style={{
                            fontSize: '12px',
                            borderRadius: '4px',
                            alignSelf: 'flex-end',
                            marginTop: '4px',
                          }}
                        >
                          Ajouter un créneau
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};

export default AvailabilityHoursModal;
